import type { WirePeer } from '../../../shared/protocol';
import type { Peer, TransportPath } from './contract';

export interface InternalPeer extends Peer {
  ecdhPublicKey?: JsonWebKey;
}

function clone<T>(value: T): T {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}

export class PeerManager {
  private readonly peers = new Map<string, InternalPeer>();

  get(peerId: string): InternalPeer | undefined {
    return this.peers.get(peerId);
  }

  has(peerId: string): boolean {
    return this.peers.has(peerId);
  }

  delete(peerId: string): boolean {
    return this.peers.delete(peerId);
  }

  getPublicKey(peerId: string): JsonWebKey | undefined {
    return this.peers.get(peerId)?.ecdhPublicKey;
  }

  update(wirePeers: WirePeer[]): void {
    const activeIds = new Set<string>();
    for (const wp of wirePeers) {
      activeIds.add(wp.deviceId);
      const existing = this.peers.get(wp.deviceId);
      const peer: InternalPeer = {
        id: wp.deviceId,
        name: wp.name,
        avatar: wp.avatar,
        color: wp.color,
        kind: wp.kind,
        platform: wp.platform,
        online: wp.online,
        sources: wp.sources,
        path: existing?.path ?? 'connecting',
        autoAccept: existing?.autoAccept ?? wp.autoAccept,
        ecdhPublicKey: wp.ecdhPublicKey ?? existing?.ecdhPublicKey,
      };
      this.peers.set(peer.id, peer);
    }
    for (const id of this.peers.keys()) {
      if (!activeIds.has(id)) {
        this.peers.delete(id);
      }
    }
  }

  setPath(peerId: string, path: TransportPath): void {
    const peer = this.peers.get(peerId);
    if (peer) {
      peer.path = path;
    }
  }

  setAutoAccept(peerId: string, autoAccept: boolean): void {
    const peer = this.peers.get(peerId);
    if (peer) {
      peer.autoAccept = autoAccept;
    }
  }

  getPublicPeers(): Peer[] {
    return [...this.peers.values()].map((internal) => {
      const { ecdhPublicKey: _ignored, ...publicPeer } = internal;
      return clone(publicPeer);
    });
  }

  values(): IterableIterator<InternalPeer> {
    return this.peers.values();
  }
}
